import _ from "../i18n";

// The gopeople API answers with three different error shapes, which is why the
// same failure could surface as "Request failed with status code 400", as a raw
// English sentence, or as a bare "Erreur" depending on the caller:
//
//   { errors: [{ field, message }], type }  validation middleware, carries fields
//   { code, message, type }                 apperrors ResponseError
//   { error, type }                         one ad hoc case of the middleware
//
// Only the first shape names the field at fault, and all three carry technical
// English text that must not be shown to the user. parseApiError turns any of
// them into field errors plus, when nothing could be attached to a field, one
// general message.

// Canonical field names, so a form can map them onto its own error keys. The
// backend is inconsistent about casing: the regex validation reports the Go
// field name ("Phone") while the custom validation reports "phone", hence the
// lookup on the lower-cased value.
const FIELD_MESSAGES = {
  firstname: "validate_name",
  lastname: "validate_name",
  phone: "validate_phone_format",
  email: "validate_email",
};

// Messages the API returns without a field name, but which are really about one
// field. Deliberately limited to the ones observed during the cross-testing
// session, matched on a fragment because the backend prefixes them
// ("Bad request. Reason: ...").
const MESSAGE_FIELDS = [
  { fragment: "user_id or email", field: "email", message: "required_field" },
  {
    fragment: "email or phone is required",
    field: "email",
    message: "required_field",
  },
];

// Several api helpers replace the axios failure with a plain Error, which loses
// the response and leaves the caller unable to tell which field is at fault.
// This keeps the message they already produced, so existing callers reading
// e.message are unaffected, and carries the status and body alongside it.
export const buildApiError = (error, fallbackMessage) => {
  const built = new Error(
    error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      fallbackMessage
  );
  built.status = error?.response?.status;
  built.data = error?.response?.data;
  return built;
};

const readErrorBody = (error) => {
  // error.data is set by the api helpers that wrap the failure in an Error,
  // error.response.data is there when the raw axios error reaches the caller.
  return error?.data ?? error?.response?.data ?? null;
};

export const parseApiError = (error) => {
  const body = readErrorBody(error);
  const fieldErrors = {};

  if (body && Array.isArray(body.errors)) {
    body.errors.forEach((item) => {
      const key = String(item?.field ?? "").toLowerCase();
      if (FIELD_MESSAGES[key]) {
        fieldErrors[key] = _(FIELD_MESSAGES[key]);
      }
    });
  }

  const rawMessage = body?.message || body?.error || "";
  if (Object.keys(fieldErrors).length === 0 && rawMessage) {
    const lowered = rawMessage.toLowerCase();
    const known = MESSAGE_FIELDS.find((entry) =>
      lowered.includes(entry.fragment)
    );
    if (known) {
      fieldErrors[known.field] = _(known.message);
    }
  }

  if (Object.keys(fieldErrors).length > 0) {
    // The detail is already shown under the fields, the toast only points at them.
    return { fieldErrors, message: _("check_form_errors") };
  }

  // Some endpoints answer with a message that is itself an i18n key, such as
  // folder.uen.not.unique. _() returns its argument unchanged when the key is
  // unknown, so a value that differs from the input is a real translation and is
  // safe to display.
  const candidates = [rawMessage];
  if (body && Array.isArray(body.errors)) {
    body.errors.forEach((item) => candidates.push(item?.message));
  }
  const translated = candidates.find(
    (candidate) => candidate && _(candidate) !== candidate
  );
  if (translated) {
    return { fieldErrors, message: _(translated) };
  }

  // Nothing could be attached to a field and nothing is translatable. Never
  // surface the backend sentence, it is technical English.
  return { fieldErrors, message: _("error") };
};
